import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import { colors } from '../../theme/theme';
import { useBusinessSearch, BusinessPlace } from '../../hooks/useBusinessSearch';
import { useAuthService } from '../../hooks/useAuthService';

interface LocationCoordinates {
  latitude: number;
  longitude: number;
}

const SearchBusinessScreen: React.FC = () => {
  const navigate = useNavigate();
  const { getSetupStatus, clearLoginDetails } = useAuthService();
  const [userLocation, setUserLocation] = useState<LocationCoordinates | null>(null);
  const [isCheckingSetup, setIsCheckingSetup] = useState(true);

  // Check setup status - redirect if business already exists or token expired
  useEffect(() => {
    const checkSetup = async () => {
      try {
        const setupResponse = await getSetupStatus();
        if (setupResponse.data.has_business) {
          // Already has business - redirect to pricing
          navigate('/dashboard', { replace: true });
          return;
        }
        setIsCheckingSetup(false);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          // Token expired or invalid — logout and redirect to login
          clearLoginDetails();
          navigate('/login', { replace: true });
          return;
        }
        console.error('Failed to check setup status:', err);
        setIsCheckingSetup(false);
      }
    };

    checkSetup();
  }, [getSetupStatus, clearLoginDetails, navigate]);

  // Get user's current location
  useEffect(() => {
    if (isCheckingSetup) return;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.warn('Error getting location:', error);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, [isCheckingSetup]);

  const {
    searchQuery,
    businesses,
    isLoading,
    hasSearched,
    handleSearchChange,
    handleBusinessSelect,
  } = useBusinessSearch({ location: userLocation });
    console.log("🚀 ~ SearchBusinessScreen ~ businesses:", businesses)

  const handleSelectBusiness = useCallback(
    async (business: BusinessPlace): Promise<void> => {
      const placeDetails = await handleBusinessSelect(business);
      if (placeDetails) {
        // Navigate to AddBusiness screen with pre-filled data
        navigate('/add-business', { state: { placeDetails } });
      }
    },
    [handleBusinessSelect, navigate]
  );

  const handleEnterManually = useCallback((): void => {
    navigate('/add-business');
  }, [navigate]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleSearchChange(event.target.value);
    },
    [handleSearchChange]
  );

  const showEmptyState = !searchQuery.trim() || (!isLoading && !hasSearched);
  const showNoResults =
    hasSearched && !isLoading && businesses.length === 0 && searchQuery.trim();
  const showResults = businesses.length > 0 && !isLoading;

  // Show loading while checking setup status
  if (isCheckingSetup) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.backgroundDefault,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: colors.backgroundDefault,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 4,
        px: 2,
        pb: 4,
      }}
    >
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 4, maxWidth: 768 }}>
        {/* Icon */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 2,
            background: `linear-gradient(180deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <BusinessIcon sx={{ color: colors.textWhite, fontSize: 28 }} />
        </Box>

        {/* Title */}
        <Typography
          sx={{
            color: colors.textPrimary,
            fontWeight: 700,
            fontSize: 36,
            lineHeight: '40px',
            mb: 1,
          }}
        >
          Find Your Business
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            color: colors.textSecondary,
            fontSize: 16,
            lineHeight: '24px',
          }}
        >
          Search for your business or add it manually
        </Typography>
      </Box>

      {/* Search Section */}
      <Box sx={{ width: '100%', maxWidth: 768, mb: 2 }}>
        {/* Search Input */}
        <TextField
          fullWidth
          value={searchQuery}
          onChange={handleInputChange}
          placeholder="Search by business name, address..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.textTertiary, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              height: 56,
              borderRadius: '14px',
              backgroundColor: colors.backgroundPaper,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.inputBorder,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary,
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '16px 16px 16px 0',
              fontSize: 16,
              '&::placeholder': {
                color: colors.textTertiary,
                opacity: 1,
              },
            },
          }}
        />

        {/* Add Business Manually Button */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleEnterManually}
          sx={{
            height: 56,
            borderRadius: '14px',
            borderColor: colors.primary,
            color: colors.primary,
            fontWeight: 600,
            fontSize: 16,
            textTransform: 'none',
            '&:hover': {
              borderColor: colors.primaryDark,
              backgroundColor: colors.primaryLight,
            },
          }}
        >
          Add Business Manually
        </Button>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      )}

      {/* Search Results */}
      {showResults && (
        <Box sx={{ width: '100%', maxWidth: 768 }}>
          {/* Section Header */}
          <Typography
            sx={{
              color: colors.textSecondary,
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: '0.7px',
              textTransform: 'uppercase',
              mb: 1.5,
            }}
          >
            Nearby Businesses
          </Typography>

          {/* Business Cards */}
          {businesses.map((business) => (
            <Card
              key={business.placeId}
              onClick={() => handleSelectBusiness(business)}
              sx={{
                px: 2.5,
                py: 2,
                mt: 1,
                mb: 1.5,
                borderRadius: '14px',
                border: `1px solid ${colors.border}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: colors.primary,
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {/* Icon */}
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '14px',
                    backgroundColor: '#EFF6FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <BusinessIcon sx={{ color: colors.primary, fontSize: 24 }} />
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  {/* Business Name */}
                  <Typography
                    noWrap
                    sx={{
                      color: colors.textPrimary,
                      fontWeight: 600,
                      fontSize: 16,
                      lineHeight: 1.4,
                      mb: 0.75,
                    }}
                  >
                    {business.name}
                  </Typography>

                  {/* Address */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.75 }}>
                    <LocationOnOutlinedIcon
                      sx={{ color: colors.textSecondary, fontSize: 16, flexShrink: 0 }}
                    />
                    <Typography
                      sx={{
                        color: colors.textSecondary,
                        fontWeight: 400,
                        fontSize: 13,
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {business.address}
                    </Typography>
                  </Box>

                  {/* Distance */}
                  {business.distance && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PlaceOutlinedIcon
                        sx={{ color: colors.textTertiary, fontSize: 14, flexShrink: 0 }}
                      />
                      <Typography
                        sx={{
                          color: colors.textTertiary,
                          fontWeight: 500,
                          fontSize: 12,
                          lineHeight: 1.4,
                        }}
                      >
                        {business.distance}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      {/* No Results State */}
      {showNoResults && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography
            sx={{
              color: colors.textSecondary,
              fontSize: 16,
              mb: 2,
            }}
          >
            No businesses found. Try a different search or
          </Typography>
          <Button
            variant="text"
            onClick={handleEnterManually}
            sx={{
              color: colors.primary,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Enter Manually
          </Button>
        </Box>
      )}

      {/* Empty State */}
      {showEmptyState && !isLoading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography
            sx={{
              color: colors.textSecondary,
              fontSize: 16,
            }}
          >
            Start typing to search for your business
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SearchBusinessScreen;
